import React, { useState, useRef, useEffect, useCallback } from "react";
import { extractDOM, serializeForLLM } from "./dom";
import { executeStructuredAction } from "./executor";
import ChatPanel from "./ChatPanel";
import { ModePicker } from "./ModePicker";

const BACKEND   = process.env.REACT_APP_AGENT_BACKEND || "https://ai.gizmott.com/ai_agent";
const MAX_STEPS = 25;
const sleep     = ms => new Promise(r => setTimeout(r, ms));
let _uid = 0;
const uid = () => ++_uid;

function snapshotPage() {
  const dom         = extractDOM();
  const domText     = serializeForLLM(dom);
  const currentPage = window.location.pathname;
  return { dom, domText, currentPage };
}

function waitForSettle(quietMs = 500, maxMs = 6000) {
  return new Promise(resolve => {
    const deadline = Date.now() + maxMs;
    let timer = null;
    let resolved = false;

    const done = () => {
      if (resolved) return;
      resolved = true;
      observer.disconnect();
      resolve();
    };

    const reset = () => {
      clearTimeout(timer);
      if (Date.now() >= deadline) { done(); return; }
      timer = setTimeout(done, quietMs);
    };

    const observer = new MutationObserver(reset);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true });
    reset();
  });
}

export default function AgentPanel({ onClose, panelWidth = 380 }) {

  const [mode,           setMode]           = useState("agent");
  const [items,          setItems]          = useState([]);
  const [query,          setQuery]          = useState("");
  const [running,        setRunning]        = useState(false);
  const [waitingForUser, setWaitingForUser] = useState(false);
  const [pageCtx,        setPageCtx]        = useState("");
  const activeStepRef = useRef(null);
  const bottomRef     = useRef(null);
  const inputRef      = useRef(null);
  const abortRef      = useRef(null);
  const latestQueryRef = useRef("");
  const historyDeltaRef = useRef([]);
  const sessionIdRef  = useRef(
    (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : `session-${Date.now()}-${Math.random().toString(36).slice(2)}`
  );
  const lastActionRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [items]);
  useEffect(() => { if (!running) inputRef.current?.focus(); }, [running]);

  useEffect(() => {
    const id = setInterval(() => {
      if (!running) {
        setPageCtx(window.location.pathname);
      }
    }, 1000);
    return () => clearInterval(id);
  }, [running]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    const goal = params.get("agentGoal");
    const autoRun = params.get("autoRun") === "true";

    if (!goal) return;

    if (autoRun) {
      setTimeout(() => {
        latestQueryRef.current = goal;
        handleAutoSend(goal);
      }, 800);
    } else {
      setItems([
        {
          id: Date.now(),
          kind: "user",
          text: goal
        }
      ]);
    }

    window.history.replaceState(
      {},
      document.title,
      window.location.pathname
    );
  }, []);

  useEffect(() => {
    const CSS_ID  = "ai-agent-glow-css";
    const GLOW_ID = "ai-agent-glow";
    const BAR_ID  = "ai-agent-action-bar";

    if (running) {
      if (!document.getElementById(CSS_ID)) {
        const s = document.createElement("style");
        s.id = CSS_ID;
        s.textContent = `
          #${GLOW_ID}-top, #${GLOW_ID}-bottom, #${GLOW_ID}-left, #${GLOW_ID}-right {
            position: fixed; pointer-events: none; z-index: 99990;
            animation: aiGlowPulse 2.2s ease-in-out infinite;
          }
          #${GLOW_ID}-top    { top:0;left:0;right:0;height:120px;background:linear-gradient(180deg,rgba(79,70,229,.75) 0%,rgba(99,102,241,.35) 35%,transparent 100%); }
          #${GLOW_ID}-bottom { bottom:40px;left:0;right:0;height:80px;background:linear-gradient(0deg,rgba(79,70,229,.55) 0%,rgba(99,102,241,.2) 40%,transparent 100%); }
          #${GLOW_ID}-left   { top:0;left:0;bottom:0;width:80px;background:linear-gradient(90deg,rgba(79,70,229,.6) 0%,rgba(99,102,241,.25) 40%,transparent 100%); }
          #${GLOW_ID}-right  { top:0;right:0;bottom:0;width:80px;background:linear-gradient(270deg,rgba(79,70,229,.6) 0%,rgba(99,102,241,.25) 40%,transparent 100%); }
          @keyframes aiGlowPulse { 0%,100%{opacity:.55}50%{opacity:1} }
          #${BAR_ID} {
            position:fixed;bottom:0;left:0;right:0;height:40px;z-index:99995;
            background:linear-gradient(90deg,rgba(10,10,30,.97) 0%,rgba(30,27,75,.97) 50%,rgba(10,10,30,.97) 100%);
            border-top:1px solid rgba(99,102,241,.5);backdrop-filter:blur(20px);
            display:flex;align-items:center;gap:10px;padding:0 18px;
            color:#a5b4fc;font-size:13px;font-weight:500;
            font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;
            box-shadow:0 -4px 24px rgba(79,70,229,.25);
          }
          @keyframes aiBarDot { 0%,80%,100%{transform:scale(.4);opacity:.4}40%{transform:scale(1);opacity:1} }
          .ai-bar-dot { display:inline-block;width:6px;height:6px;border-radius:50%;background:#818cf8;animation:aiBarDot 1.2s infinite; }
          .ai-bar-dot:nth-child(2){animation-delay:.2s}.ai-bar-dot:nth-child(3){animation-delay:.4s}
          .ai-bar-icon { width:20px;height:20px;margin-right:4px;border:2px solid rgba(99,102,241,.4);border-top-color:#818cf8;border-radius:50%;animation:aiBarSpin .8s linear infinite;flex-shrink:0; }
          @keyframes aiBarSpin { to{transform:rotate(360deg)} }
        `;
        document.head.appendChild(s);
      }
      for (const sfx of ["-top", "-bottom", "-left", "-right"]) {
        const id = GLOW_ID + sfx;
        if (!document.getElementById(id)) {
          const el = document.createElement("div");
          el.id = id; el.setAttribute("data-agent-ui", "true");
          document.body.appendChild(el);
        }
      }
      if (!document.getElementById(BAR_ID)) {
        const bar = document.createElement("div");
        bar.id = BAR_ID; bar.setAttribute("data-agent-ui", "true");
        bar.innerHTML = `<span class="ai-bar-icon"></span><span class="ai-bar-dot"></span><span class="ai-bar-dot"></span><span class="ai-bar-dot"></span><span id="ai-bar-text" style="margin-left:8px">Working…</span>`;
        document.body.appendChild(bar);
      }
    } else {
      for (const sfx of ["-top", "-bottom", "-left", "-right"]) {
        document.getElementById(GLOW_ID + sfx)?.remove();
      }
      document.getElementById(BAR_ID)?.remove();
      document.getElementById(CSS_ID)?.remove();
    }
    return () => {
      for (const sfx of ["-top", "-bottom", "-left", "-right"]) {
        document.getElementById(GLOW_ID + sfx)?.remove();
      }
      document.getElementById(BAR_ID)?.remove();
      document.getElementById(CSS_ID)?.remove();
    };
  }, [running]);

  function setBarText(text) {
    const el = document.getElementById("ai-bar-text");
    if (el) el.textContent = text;
  }

  async function captureScreenshot() {
    try {
      if (!window.html2canvas) {
        await new Promise(resolve => {
          const s = document.createElement("script");
          s.src = "https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js";
          s.onload = resolve; s.onerror = resolve;
          document.head.appendChild(s);
        });
      }
      if (!window.html2canvas) return "";
      const canvas = await window.html2canvas(document.body, {
        scale: 0.4, useCORS: true, allowTaint: true, logging: false,
        scrollX: 0, scrollY: -window.scrollY,
        windowWidth: window.innerWidth, windowHeight: window.innerHeight,
        ignoreElements: el => !!el.getAttribute("data-agent-ui"),
        onclone: (_doc, el) => {
          el.querySelectorAll("*").forEach(node => {
            try {
              if (node.style.backgroundImage?.includes("gradient")) node.style.backgroundImage = "none";
            } catch (_) {}
          });
        },
      });
      return canvas.toDataURL("image/jpeg", 0.65);
    } catch (e) {
      console.warn("[AgentPanel] Screenshot failed:", e);
      return "";
    }
  }
  async function handleAutoSend(text) {
    if (!text || running) return;

    pushItem({
      kind: "user",
      text
    });

    abortRef.current?.abort();
    abortRef.current = new AbortController();

    latestQueryRef.current = text;

    runLoop(
      text,
      abortRef.current.signal
    );
  }

  function ensureStep(title) {
    if (activeStepRef.current) {
      return activeStepRef.current;
    }
    return startStep(title);
  }

  function pushItem(item) { setItems(prev => [...prev, { id: uid(), ...item }]); }

  function startStep(title) {
    const id = uid();
    activeStepRef.current = id;
    setItems(prev => [...prev, { kind: "step", id, title, status: "running", subSteps: [], expanded: true }]);
    return id;
  }
  function addSubStep(stepId, text, icon = "↗") {
    setItems(prev => prev.map(it =>
      it.kind === "step" && it.id === stepId
        ? { ...it, subSteps: [...it.subSteps, { text, icon }] }
        : it
    ));
  }
  function finishStep(stepId, status = "done") {
    setItems(prev => prev.map(it =>
      it.kind === "step" && it.id === stepId ? { ...it, status, expanded: status === "error" } : it
    ));
    if (activeStepRef.current === stepId) activeStepRef.current = null;
  }
  function toggleStep(stepId) {
    setItems(prev => prev.map(it =>
      it.kind === "step" && it.id === stepId ? { ...it, expanded: !it.expanded } : it
    ));
  }

  function handleClose() {
    sessionIdRef.current = crypto.randomUUID();
    setItems([]);
    setWaitingForUser(false);
    abortRef.current?.abort();
    onClose();
    historyDeltaRef.current = [];
  }

  function _collectValidationErrors() {
    const errors = [], seen = new Set();
    const candidates = document.querySelectorAll(
      '[class*="error"],[class*="Error"],[class*="invalid"],[class*="Invalid"],' +
      '[class*="required"],[class*="Required"],[aria-invalid="true"] + *,[role="alert"]'
    );
    for (const el of candidates) {
      if (el.closest("[data-agent-ui]")) continue;
      const s = window.getComputedStyle(el);
      if (s.display === "none" || s.visibility === "hidden") continue;
      const r = el.getBoundingClientRect();
      if (r.width === 0 || r.height === 0) continue;
      const text = el.innerText?.replace(/\s+/g, " ").trim();
      if (!text || text.length < 3 || text.length > 120) continue;
      if (!/required|invalid|missing|must|error|cannot|please|select|choose|provide/i.test(text)) continue;
      if (seen.has(text)) continue;
      seen.add(text);
      errors.push(text);
      if (errors.length >= 10) break;
    }
    return errors;
  }

  const runLoop = async (initialQuery, signal) => {
    latestQueryRef.current = initialQuery;
    setRunning(true);
    try{
    const perPageSeen  = new Map();
    const globalSeen   = new Map();
    let currentStepId  = null;
    let hasThought     = false;
    let lastUrl        = window.location.href;

    const closeStep = (status = "done") => {
      if (currentStepId) { finishStep(currentStepId, status); currentStepId = null; activeStepRef.current = null; }
    };

    for (let step = 0; step < MAX_STEPS; step++) {
      if (signal.aborted) break;

      const nowUrl = window.location.href;
      if (nowUrl !== lastUrl) {
        lastUrl = nowUrl;
        perPageSeen.clear();   
        closeStep("done");
        hasThought = false;
        await waitForSettle(400, 3000);
      }

      const { dom, domText, currentPage } = snapshotPage();
      setPageCtx(currentPage);
      setBarText("Thinking…");

      const screenshot = await captureScreenshot();
      let action;
      try {
        const res = await fetch(`${BACKEND}/decide`, {
          method:  "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            query:        latestQueryRef.current,
            dom,                    
            dom_text:     domText,  
            session_id:   sessionIdRef.current,
            current_page: currentPage,  
            screenshot,
            last_action:
              lastActionRef.current?.action || "",

            last_action_success:
              lastActionRef.current?.success || false,

            history_delta:
              step === 0
                ? [
                    {
                      role: "user",
                      content: latestQueryRef.current
                    },
                    ...historyDeltaRef.current
                  ]
                : historyDeltaRef.current,
          }),
          signal,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        action = typeof data.response === "string" ? JSON.parse(data.response) : data.response;
        console.log(
          "AGENT RESPONSE",
          action
        );
      } catch (err) {
        if (err.name === "AbortError") break;
        const sid = ensureStep("Error");
        addSubStep(sid, `Cannot reach server: ${err.message}`, "⚠️");
        finishStep(sid, "error");
        break;
      }

      if (action.action !== "think") {
        const guardKey     = `${action.action}::${action.selector || action.text || action.target || action.question || ""}`;
        const perPageCount = (perPageSeen.get(guardKey) || 0) + 1;
        const globalCount  = (globalSeen.get(guardKey) || 0) + 1;
        perPageSeen.set(guardKey, perPageCount);
        globalSeen.set(guardKey, globalCount);

        
          if (perPageCount > 2 || globalCount > 4) {
            pushItem({
              kind: "error",
              text: "Agent stopped because no further progress was detected."
            });

            setRunning(false);
            return;
          }
      
    }
      if (action.action === "think") {
        if (hasThought) { await sleep(150); continue; }
        hasThought = true;
        closeStep("done");
        const title = action.plan?.[0] || action.thought?.slice(0, 70) || "Planning…";
        currentStepId = startStep(title);
        activeStepRef.current = currentStepId;
        setBarText(title);
        if (action.plan?.length > 1) action.plan.slice(1, 5).forEach(p => addSubStep(currentStepId, p, "·"));
        await sleep(200);
        continue;
      }

      if (action.action === "click") {
        const desc = action.description || `Click "${action.text || action.selector}"`;
        const sid = startStep(desc);
        currentStepId = sid;
        setBarText(`Clicking ${action.text || action.selector}…`);
        addSubStep(sid, desc, "↗");

        const result =
          await executeStructuredAction(
            action
          );

        await waitForSettle(500, 5000);
        if (!result.found || !result.success) {
          addSubStep(sid, `Not found: "${action.text || action.selector}"`, "❌");
          finishStep(sid, "error");
          currentStepId = null;
        } else {

          const waitMs = action.wait_ms ?? 1000;

          await sleep(Math.min(waitMs, 1500));

          await waitForSettle(400, 5000);
          const pageText = document.body.innerText.toLowerCase();

          const actionText =
            (action.text || action.description || "").toLowerCase();

          const isSearchAction =
            actionText.includes("apply filter") ||
            actionText.includes("search");

          if (
            isSearchAction &&
            pageText.includes("0 total") &&
            pageText.includes("no data available")
          ) {
            pushItem({
              kind: "done",
              text: "Search completed. No matching records found."
            });

            setRunning(false);
            return;
          }

          addSubStep(
            sid,
            "Action completed",
            "✅"
          );

          finishStep(sid, "done");

          currentStepId = null;

          lastActionRef.current = {
            action:
              action.description ||
              action.text ||
              action.selector,
            success: true
          };

          historyDeltaRef.current.push({
            role: "assistant",
            content: JSON.stringify({
              action: "click",
              description:
                action.description ||
                action.text ||
                action.selector,
              current_page:
                window.location.pathname,
              success: true,

          
                
            })
          });

          const clickedText = (action.text || action.description || "").toLowerCase();
          if (/save|submit|create|publish|update/i.test(clickedText)) {
            const validationErrors = _collectValidationErrors();
            if (validationErrors.length > 0) {
              const warnSid = startStep("Validation errors detected");
              validationErrors.forEach(e => addSubStep(warnSid, e, "❌"));
              finishStep(warnSid, "error");
            }
          }
        }
        continue;
      }

      if (action.action === "fill") {
        const desc = action.description || `Fill "${action.text || action.selector}"`;
        const sid = startStep(desc);
        currentStepId = sid;
        setBarText(`Filling "${action.text || action.selector}"…`);
        addSubStep(sid, `${desc}: "${String(action.value || "").slice(0, 50)}"`, "✏️");
        const result =
          await executeStructuredAction(
            action
          );

        await waitForSettle(500, 5000);
        const pageText = document.body.innerText.toLowerCase();

        if (!result.found || !result.success) {
          addSubStep(sid, `Field not found: "${action.text || action.selector}"`, "❌");
          finishStep(sid, "error");
          currentStepId = null;
        } else {

          addSubStep(
            sid,
            "Field filled successfully",
            "✅"
          );

          finishStep(sid, "done");

          currentStepId = null;

          lastActionRef.current = {
            action:
              action.description ||
              action.text ||
              action.selector,
            success: true
          };

          historyDeltaRef.current.push({
            role: "assistant",
            content: JSON.stringify({
              action: "fill",
              description:
                action.description ||
                action.text ||
                action.selector,
              value: action.value,
              current_page:
                window.location.pathname,
              success: true,
            })
          });
        }
        await sleep(action.wait_ms ?? 400);
        continue;
      }

      if (action.action === "select") {
        const label = action.text || action.target || "dropdown";
        const val   = action.value || action.input || "";
        const desc  = action.description || `Select "${val}" from "${label}"`;
        const sid = startStep(desc);
        currentStepId = sid;
        setBarText(`Selecting "${val}"…`);
        addSubStep(sid, desc, "🔽");
        const result =
          await executeStructuredAction(
            action
          );

        await waitForSettle(500, 5000);
        const pageText = document.body.innerText.toLowerCase();

        
        if (!result.found || !result.success) {
          addSubStep(sid, `Dropdown not found: "${label}"`, "❌");
          finishStep(sid, "error");
          currentStepId = null;
        } else {

          addSubStep(
            sid,
            "Field filled successfully",
            "✅"
          );

          finishStep(sid, "done");

          currentStepId = null;

          lastActionRef.current = {
            action:
              action.description ||
              action.text ||
              action.selector,
            success: true
          };

          historyDeltaRef.current.push({
            role: "assistant",
            content: JSON.stringify({
              action: "select",
              description:
                action.description ||
                action.text ||
                action.selector,
              value:
                action.value ||
                action.input,
              current_page:
                window.location.pathname,
              success: true,
            })
          });
        }
        await sleep(action.wait_ms ?? 800);

        await waitForSettle(500, 5000);

        const freshDom = snapshotPage();

        setPageCtx(freshDom.currentPage);

        continue;
      }

      if (action.action === "toggle") {
        const label = action.text || action.description || "toggle";
        const sid   = ensureStep(action.description || `Toggle "${label}"`);
        setBarText(`Toggling "${label}"…`);
        addSubStep(sid, `Toggle "${label}"`, "🔘");
        const result =
          await executeStructuredAction(
            action
          );

        await waitForSettle(500, 5000);
        if (!result.found || !result.success) {
          addSubStep(sid, `Not found: "${label}"`, "❌");
          finishStep(sid, "error");
          currentStepId = null;
        } else {

          addSubStep(
            sid,
            "Toggle completed",
            "✅"
          );

          finishStep(sid, "done");

          currentStepId = null;
        }
        await sleep(action.wait_ms ?? 400);

        await waitForSettle(500, 5000);

        const freshDom = snapshotPage();

        setPageCtx(freshDom.currentPage);

        continue;
      }

      if (action.action === "image_fetch") {
        const sid = ensureStep(action.description || `Fetch image: "${action.query}"`);
        setBarText(`Searching image: "${action.query}"…`);
        addSubStep(sid, `Searching web for: "${action.query}"`, "🖼️");
        try {
          const imgRes = await fetch(`${BACKEND}/image-fetch`, {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ query: action.query, aspect_ratio: action.aspect_ratio || "16:9", selector: action.selector || "" }),
            signal: AbortSignal.timeout(30000),
          });
          if (!imgRes.ok) throw new Error(`HTTP ${imgRes.status}`);
          const imgData = await imgRes.json();
          if (imgData.error) throw new Error(imgData.error);
          const up = executeStructuredAction({ action: "upload_file", selector: action.selector, data_url: imgData.data_url, filename: `${(action.query || "image").replace(/\s+/g, "-")}.png` });
          if (!up?.found) addSubStep(sid, "Upload field not found", "❌");
          else addSubStep(sid, `Uploaded ${imgData.width}×${imgData.height}`, "✅");
        } catch (err) { addSubStep(sid, `Image fetch failed: ${err.message}`, "❌"); }
        await sleep(action.wait_ms ?? 800);
        continue;
      }

      if (action.action === "web_content_fetch") {
        const sid = ensureStep(action.description || `Fetch description: "${action.query}"`);
        setBarText(`Fetching description for "${action.query}"…`);
        addSubStep(sid, `Web search: "${action.query}"`, "🔍");
        try {
          const cRes = await fetch(`${BACKEND}/content-fetch`, {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ query: action.query }),
            signal: AbortSignal.timeout(30000),
          });
          if (!cRes.ok) throw new Error(`HTTP ${cRes.status}`);
          const cData = await cRes.json();
          if (cData.error) throw new Error(cData.error);
          const fillResult = executeStructuredAction({ action: "fill", selector: action.field_selector, text: action.field_text, value: cData.content });
          if (!fillResult?.found) addSubStep(sid, "Description field not found", "❌");
          else addSubStep(sid, `Filled description (${cData.content.length} chars)`, "✅");
        } catch (err) { addSubStep(sid, `Description fetch failed: ${err.message}`, "❌"); }
        await sleep(action.wait_ms ?? 1000);
        continue;
      }

      if (action.action === "ask") {
        closeStep("waiting");
        pushItem({ kind: "ask", text: action.question || "Please provide more details.", field: action.field });
        setWaitingForUser(true);
        setRunning(false);
        return;
      }

      if (action.action === "done") {
        closeStep("done");

        pushItem({
          kind: "done",
          text: action.message || "Task completed!"
        });

        setRunning(false);

        return;
      }

      if (action.action === "report") {

        closeStep("done");

        pushItem({
          kind: "report",
          text: action.message || ""
        });

        setRunning(false);

        return;
      }

      if (action.action === "open_tab") {

        const sid = startStep(
          action.description || "Opening page"
        );

        const targetUrl =
          action.url.startsWith("http")
            ? action.url
            : `${window.location.origin}${action.url}`;
        

        window.open(
          targetUrl,
          "_blank",
          "noopener,noreferrer"
        );

        finishStep(sid, "done");

        return;
      }

      console.warn("[AI Agent] Unhandled action:", action);
      break;
    }
      
    } 
    finally {

      if (activeStepRef.current) {
        finishStep(activeStepRef.current, "done");
      }

      setRunning(false);
    }
  };

  async function handleSend() {
    const text = query.trim();
    if (!text || running) return;
    setQuery("");
    pushItem({ kind: "user", text });
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    if (!waitingForUser) {
      sessionIdRef.current = crypto.randomUUID();
      setItems(prev => [prev[prev.length - 1]].filter(Boolean));
    }
    latestQueryRef.current = text;
    setWaitingForUser(false);
    runLoop(text, abortRef.current.signal);
  }

  return (
    <div style={{ ...S.panel, width: panelWidth }} data-agent-ui="true">
      <div style={S.header}>
        <div style={S.headerLeft}>
          <span style={S.headerIcon}>✦</span>
          <span style={S.headerTitle}>AI Assistant</span>
          {mode === "agent" && pageCtx && <span style={S.pageCtx}>📄 {pageCtx}</span>}
        </div>
        <button onClick={handleClose} style={S.closeBtn} aria-label="Close">✕</button>
      </div>

      {mode === "agent" ? (<>
      <div style={S.chat} className="ai-chat-scroll">
        <div style={S.chatInner}>
        {items.length === 0 && (
          <div style={S.empty}>
            <div style={S.emptyIcon}>✦</div>
            <p style={S.emptyTitle}>AI Assistant</p>
            <p style={S.emptyDesc}>Tell me what to do — I'll navigate, fill forms, and complete tasks automatically.</p>
            <div style={S.suggestionGrid}>
              {["Go to Shows", "Create a new video", "Get the Revenue report", "Open Viewership"].map(s => (
                <button key={s} style={S.suggestion} onClick={() => { setQuery(s); inputRef.current?.focus(); }}>{s}</button>
              ))}
            </div>
          </div>
        )}
        {items.map(item => {
          if (item.kind === "user") return <div key={item.id} style={{ ...S.bubble, ...S.userBubble }}>{item.text}</div>;
          if (item.kind === "step") return (
            <div key={item.id} style={{ ...S.stepCard, borderColor: item.status === "running" ? "rgba(99,102,241,0.45)" : item.status === "error" ? "rgba(239,68,68,0.35)" : item.status === "waiting" ? "rgba(251,191,36,0.35)" : "rgba(255,255,255,0.08)" }}>
              <button style={S.stepHeader} onClick={() => toggleStep(item.id)}>
                <span style={S.stepIconWrap}>
                  {item.status === "running" && <span style={S.stepSpinner} />}
                  {item.status === "done"    && <span style={{ color: "#4ade80", fontSize: 14 }}>✓</span>}
                  {item.status === "error"   && <span style={{ color: "#f87171", fontSize: 14 }}>✕</span>}
                  {item.status === "waiting" && <span style={{ color: "#fbbf24", fontSize: 14 }}>?</span>}
                </span>
                <span style={{ ...S.stepTitle, color: item.status === "running" ? "#e2e8f0" : item.status === "error" ? "#fca5a5" : item.status === "waiting" ? "#fde68a" : "#94a3b8" }}>{item.title}</span>
                <span style={{ ...S.stepChevron, transform: item.expanded ? "rotate(180deg)" : "none" }}>⌄</span>
              </button>
              {item.expanded && item.subSteps.length > 0 && (
                <div style={S.subSteps}>
                  {item.subSteps.map((sub, i) => (
                    <div key={i} style={S.subStep}>
                      <span style={S.subStepIcon}>{sub.icon}</span>
                      <span style={S.subStepText}>{sub.text}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
          if (item.kind === "ask")    return <div key={item.id} style={S.askCard}><div style={S.askIcon}>?</div><div style={S.askText}>{item.text}</div></div>;
          if (item.kind === "done")   return <div key={item.id} style={{ ...S.bubble, ...S.doneBubble }}>✅ {item.text}</div>;
          if (item.kind === "error")  return <div key={item.id} style={{ ...S.bubble, ...S.errorBubble }}>⚠️ {item.text}</div>;
          if (item.kind === "report") return <div key={item.id} style={{ ...S.bubble, ...S.reportBubble }}>{item.text}</div>;
          return null;
        })}
        {running && (items.length === 0 || items[items.length - 1]?.kind === "user") && (
          <div style={S.thinkingRow}>
            <span style={S.dot} /><span style={{ ...S.dot, animationDelay: "0.2s" }} /><span style={{ ...S.dot, animationDelay: "0.4s" }} />
          </div>
        )}
        <div ref={bottomRef} />
        </div>
      </div>
      <div style={S.footer}>
        {waitingForUser && <div style={S.waitHint}>💬 Waiting for your reply</div>}
        <ModePicker mode={mode} setMode={setMode} />
        <div style={S.inputRow}>
          <textarea
            ref={inputRef} value={query} rows={1}
            onChange={e => { setQuery(e.target.value); e.target.style.height = "auto"; e.target.style.height = Math.min(e.target.scrollHeight, 100) + "px"; }}
            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
            placeholder={running ? "Agent is working…" : waitingForUser ? "Type your answer…" : "What would you like to do?"}
            style={{ ...S.input, borderColor: waitingForUser ? "#f59e0b" : "#cbd5e1" }}
            disabled={running} autoFocus
          />
          <button onClick={handleSend} disabled={running || !query.trim()} style={S.sendBtn} aria-label="Send">
            {running ? <span style={S.spinner} /> : <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M2 21l21-9L2 3v7l15 2-15 2v7z"/></svg>}
          </button>
        </div>
      </div>
      </>) : (<ChatPanel mode={mode} setMode={setMode} />)}

      <style>{`
        @keyframes ai-think   { 0%,80%,100%{transform:scale(0);opacity:.3}40%{transform:scale(1);opacity:1} }
        @keyframes ai-spin    { to{transform:rotate(360deg)} }
        @keyframes ai-slideIn { from{transform:translateX(100%)}to{transform:translateX(0)} }
        @keyframes ai-fadeIn  { from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:none} }
        @keyframes ai-stepSpin{ to{transform:rotate(360deg)} }
        [data-agent-ui] .ai-chat-scroll::-webkit-scrollbar{width:5px}
        [data-agent-ui] .ai-chat-scroll::-webkit-scrollbar-track{background:transparent}
        [data-agent-ui] .ai-chat-scroll::-webkit-scrollbar-thumb{background:rgba(0,0,0,.15);border-radius:4px}
      `}</style>
    </div>
  );
}

const S = {
  panel:      { position:"fixed",top:0,right:0,height:"100vh",display:"flex",flexDirection:"column",background:"#ffffff",borderLeft:"1px solid #e2e8f0",boxShadow:"-8px 0 48px rgba(138,195,230,.18),-2px 0 16px rgba(60,116,221,.12)",zIndex:99998,overflow:"hidden",animation:"ai-slideIn 0.22s ease",fontFamily:"-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif" },
  header:     { display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 14px",background:"linear-gradient(135deg,rgba(104,183,240,.08),rgba(35,116,230,.05))",borderBottom:"1px solid #e2e8f0",flexShrink:0 },
  headerLeft: { display:"flex",alignItems:"center",gap:8,overflow:"hidden" },
  headerIcon: { fontSize:18,color:"#6366f1" },
  headerTitle:{ color:"#1e293b",fontWeight:600,fontSize:14,letterSpacing:0.3 },
  pageCtx:    { fontSize:11,color:"#64748b",background:"rgba(99,102,241,.07)",borderRadius:6,padding:"2px 7px",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:160 },
  closeBtn:   { background:"none",border:"none",color:"#64748b",cursor:"pointer",padding:"4px 6px",borderRadius:6,fontSize:14,lineHeight:1,flexShrink:0 },
  chat:       { flex:1,minHeight:0,overflowY:"auto",scrollbarWidth:"thin",scrollbarColor:"rgba(0,0,0,.12) transparent",background:"#f8fafc" },
  chatInner:  { display:"flex",flexDirection:"column",gap:7,padding:"14px 10px",minHeight:"100%" },
  empty:      { textAlign:"center",margin:"auto",padding:"0 12px",color:"#64748b" },
  emptyIcon:  { fontSize:32,marginBottom:8,color:"#6366f1" },
  emptyTitle: { margin:"0 0 6px",color:"#1e293b",fontWeight:600,fontSize:15 },
  emptyDesc:  { margin:"0 0 14px",fontSize:13,lineHeight:1.5,color:"#64748b" },
  suggestionGrid:{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:6 },
  suggestion: { background:"rgba(99,102,241,.07)",border:"1px solid rgba(99,102,241,.2)",borderRadius:8,color:"#4f46e5",fontSize:12,padding:"7px 10px",cursor:"pointer",textAlign:"left" },
  bubble:     { padding:"9px 13px",borderRadius:12,fontSize:13,lineHeight:1.5,wordBreak:"break-word",animation:"ai-fadeIn 0.15s ease" },
  userBubble: { alignSelf:"flex-end",maxWidth:"88%",background:"linear-gradient(135deg,#6366f1,#8b5cf6)",color:"#fff",borderBottomRightRadius:4 },
  doneBubble: { alignSelf:"flex-start",background:"#f0fdf4",border:"1px solid #bbf7d0",color:"#166534" },
  errorBubble:{ alignSelf:"flex-start",background:"#fef2f2",border:"1px solid #fecaca",color:"#991b1b" },
  reportBubble:{ alignSelf:"flex-start",maxWidth:"100%",background:"#ffffff",border:"1px solid #e2e8f0",color:"#334155",whiteSpace:"pre-wrap",fontSize:12,lineHeight:1.6 },
  stepCard:   { background:"#ffffff",border:"1px solid #e2e8f0",borderRadius:10,overflow:"hidden",animation:"ai-fadeIn 0.18s ease" },
  stepHeader: { width:"100%",display:"flex",alignItems:"center",gap:9,background:"none",border:"none",cursor:"pointer",padding:"10px 12px",textAlign:"left",color:"#1e293b" },
  stepIconWrap:{ width:20,height:20,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 },
  stepSpinner:{ display:"inline-block",width:13,height:13,border:"2px solid rgba(99,102,241,.2)",borderTopColor:"#6366f1",borderRadius:"50%",animation:"ai-stepSpin 0.8s linear infinite" },
  stepTitle:  { flex:1,fontSize:13,fontWeight:500,lineHeight:1.4 },
  stepChevron:{ color:"#94a3b8",fontSize:16,transition:"transform 0.2s",flexShrink:0 },
  subSteps:   { borderTop:"1px solid #f1f5f9",padding:"6px 12px 10px 40px",display:"flex",flexDirection:"column",gap:6 },
  subStep:    { display:"flex",alignItems:"flex-start",gap:8 },
  subStepIcon:{ fontSize:11,color:"#6366f1",flexShrink:0,marginTop:2 },
  subStepText:{ fontSize:12,color:"#64748b",lineHeight:1.45 },
  askCard:    { background:"#fffbeb",border:"1px solid #fde68a",borderRadius:10,padding:"10px 14px",display:"flex",alignItems:"flex-start",gap:10,animation:"ai-fadeIn 0.18s ease" },
  askIcon:    { width:22,height:22,borderRadius:"50%",background:"#fef3c7",color:"#d97706",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,fontSize:13,flexShrink:0 },
  askText:    { color:"#92400e",fontSize:13,lineHeight:1.5 },
  thinkingRow:{ display:"flex",alignItems:"center",gap:5,padding:"10px 14px",background:"#f1f5f9",borderRadius:10,width:"fit-content" },
  dot:        { display:"inline-block",width:7,height:7,borderRadius:"50%",background:"#6366f1",animation:"ai-think 1.2s infinite" },
  footer:     { padding:"10px 12px",borderTop:"1px solid #e2e8f0",flexShrink:0,background:"#ffffff" },
  waitHint:   { fontSize:11,color:"#d97706",marginBottom:6,padding:"3px 8px",background:"#fef3c7",borderRadius:6,display:"inline-block" },
  inputRow:   { display:"flex",gap:8,alignItems:"flex-end" },
  input:      { flex:1,background:"#f8fafc",border:"1px solid #cbd5e1",borderRadius:10,color:"#1e293b",fontSize:13,padding:"9px 12px",resize:"none",outline:"none",lineHeight:1.5,minHeight:38,maxHeight:100,overflow:"hidden",transition:"border-color 0.15s",fontFamily:"inherit" },
  sendBtn:    { width:38,height:38,borderRadius:10,border:"none",background:"linear-gradient(135deg,#6366f1,#8b5cf6)",color:"#fff",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,transition:"opacity 0.15s" },
  spinner:    { display:"inline-block",width:14,height:14,border:"2px solid rgba(255,255,255,.3)",borderTopColor:"#fff",borderRadius:"50%",animation:"ai-spin 0.7s linear infinite" },
};